import { v4 as uuidv4 } from "uuid";
import { arrayMove } from "@dnd-kit/sortable";
import EmailBlockTypeEnum from "../models/EmailBlockTypeEnum";
import IEmailBlock, { IEmailColumnsBlock, IEmailLeafBlock } from "../models/IEmailBlock";
import IEmailDocument from "../models/IEmailDocument";

/** i18n key for a block type's label. */
export const blockLabelKey = (type: EmailBlockTypeEnum): string => {
    const snake = String(type).replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

    return `block_${snake}`;
};

/** FontAwesome class for a block type (palette and canvas rows). */
export const blockIconClass = (type: EmailBlockTypeEnum): string => {
    switch (type) {
        case EmailBlockTypeEnum.HEADING:
            return "fad fa-heading";
        case EmailBlockTypeEnum.TEXT:
            return "fad fa-align-left";
        case EmailBlockTypeEnum.IMAGE:
            return "fad fa-image";
        case EmailBlockTypeEnum.LOGO:
            return "fad fa-copyright";
        case EmailBlockTypeEnum.BUTTON:
            return "fad fa-hand-pointer";
        case EmailBlockTypeEnum.DIVIDER:
            return "fad fa-horizontal-rule";
        case EmailBlockTypeEnum.SPACER:
            return "fad fa-arrows-alt-v";
        case EmailBlockTypeEnum.COLUMNS:
            return "fad fa-columns";
        case EmailBlockTypeEnum.ORDER_ITEM_TABLE:
            return "fad fa-table";
        case EmailBlockTypeEnum.SHIP_TO:
            return "fad fa-map-marker-alt";
        case EmailBlockTypeEnum.ORDER_DETAILS:
            return "fad fa-info-circle";
        case EmailBlockTypeEnum.CUSTOM_MESSAGE:
            return "fad fa-comment-alt-lines";
        case EmailBlockTypeEnum.FOOTER:
            return "fad fa-shoe-prints";
        default:
            return "fad fa-square";
    }
};

/** Translates an i18n key; defaults to the English text used when no translator is given. */
export type EmailTextTranslator = (id: string) => string;

const DEFAULT_TEXT: Record<string, string> = {
    block_default_heading: "Heading",
    block_default_text: "<p>Write something…</p>",
    block_default_button_label: "View Order",
    block_default_footer: "<p>Questions? Contact us at %SUPPORT_EMAIL%.</p>",
    hides: "hides",
};

const text = (id: string, t?: EmailTextTranslator): string => (t ? t(id) : DEFAULT_TEXT[id] || id);

/** A new block with sensible defaults for its type; pass `t` so the default content is in the user's language. */
export const createBlock = (type: EmailBlockTypeEnum, t?: EmailTextTranslator): IEmailBlock => {
    const id = uuidv4();

    switch (type) {
        case EmailBlockTypeEnum.HEADING:
            return { id, type, text: text("block_default_heading", t), level: 2, align: "center" };
        case EmailBlockTypeEnum.TEXT:
            return { id, type, html: text("block_default_text", t) };
        case EmailBlockTypeEnum.IMAGE:
            return { id, type, alt: "", align: "center" };
        case EmailBlockTypeEnum.LOGO:
            return { id, type, width: 200, align: "center" };
        case EmailBlockTypeEnum.BUTTON:
            return { id, type, label: text("block_default_button_label", t), href: "%ORDER_URL%", align: "center" };
        case EmailBlockTypeEnum.DIVIDER:
            return { id, type };
        case EmailBlockTypeEnum.SPACER:
            return { id, type, height: 24 };
        case EmailBlockTypeEnum.COLUMNS:
            return { id, type, columns: [{ blocks: [] }, { blocks: [] }], widths: [50, 50] };
        case EmailBlockTypeEnum.ORDER_ITEM_TABLE:
            return { id, type, excludeCols: [] };
        case EmailBlockTypeEnum.SHIP_TO:
            return { id, type };
        case EmailBlockTypeEnum.ORDER_DETAILS:
            return { id, type };
        case EmailBlockTypeEnum.CUSTOM_MESSAGE:
            return { id, type };
        case EmailBlockTypeEnum.FOOTER:
            return { id, type, html: text("block_default_footer", t), showOrderwerksLinks: true };
        default:
            throw new Error(`Unknown block type ${type}`);
    }
};

const stripTags = (html: string): string =>
    html
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();

/** One-line description shown on a canvas row. */
export const blockSummary = (block: IEmailBlock, t?: EmailTextTranslator): string => {
    const b = block as any;

    switch (block.type) {
        case EmailBlockTypeEnum.HEADING:
            return b.text || "";
        case EmailBlockTypeEnum.TEXT:
        case EmailBlockTypeEnum.FOOTER:
            return stripTags(b.html || "").slice(0, 80);
        case EmailBlockTypeEnum.IMAGE:
            return b.url || (b.attachment_id ? `#${b.attachment_id}` : "");
        case EmailBlockTypeEnum.BUTTON:
            return `${b.label || ""} → ${b.href || ""}`;
        case EmailBlockTypeEnum.SPACER:
            return `${b.height}px`;
        case EmailBlockTypeEnum.COLUMNS:
            return `${b.columns?.[0]?.blocks?.length ?? 0} | ${b.columns?.[1]?.blocks?.length ?? 0}`;
        case EmailBlockTypeEnum.ORDER_ITEM_TABLE:
            return b.excludeCols?.length ? `${text("hides", t)}: ${b.excludeCols.join(", ")}` : "";
        default:
            return "";
    }
};

export interface IBlockLocation {
    block: IEmailBlock;
    /** Set when the block lives inside a columns block. */
    parentId?: string;
    columnIndex?: 0 | 1;
}

export const findBlock = (doc: IEmailDocument, id: string): IBlockLocation | null => {
    for (const block of doc.blocks) {
        if (block.id === id) {
            return { block };
        }

        if (block.type === EmailBlockTypeEnum.COLUMNS) {
            for (let c = 0; c < 2; c++) {
                const hit = block.columns[c]?.blocks.find((leaf) => leaf.id === id);

                if (hit) {
                    return { block: hit, parentId: block.id, columnIndex: c as 0 | 1 };
                }
            }
        }
    }

    return null;
};

/** Every block id in document order (top level and inside columns). */
export const allBlockIds = (doc: IEmailDocument): string[] => {
    const ids: string[] = [];

    for (const block of doc.blocks) {
        ids.push(block.id);

        if (block.type === EmailBlockTypeEnum.COLUMNS) {
            block.columns.forEach((col) => col.blocks.forEach((leaf) => ids.push(leaf.id)));
        }
    }

    return ids;
};

const mapBlocks = (doc: IEmailDocument, fn: (block: IEmailBlock) => IEmailBlock | null): IEmailDocument => {
    const blocks: IEmailBlock[] = [];

    for (const block of doc.blocks) {
        let next = fn(block);

        if (next && next.type === EmailBlockTypeEnum.COLUMNS) {
            next = {
                ...next,
                columns: next.columns.map((col) => ({
                    blocks: col.blocks.map((leaf) => fn(leaf)).filter((leaf): leaf is IEmailLeafBlock => !!leaf),
                })) as IEmailColumnsBlock["columns"],
            };
        }

        if (next) {
            blocks.push(next);
        }
    }

    return { ...doc, blocks };
};

/** Shallow-merge `patch` into the block with `id` (anywhere in the tree). */
export const updateBlock = (doc: IEmailDocument, id: string, patch: Partial<IEmailBlock>): IEmailDocument => {
    return mapBlocks(doc, (block) => (block.id === id ? ({ ...block, ...patch } as IEmailBlock) : block));
};

export const removeBlock = (doc: IEmailDocument, id: string): IEmailDocument => {
    return mapBlocks(doc, (block) => (block.id === id ? null : block));
};

/** Deep copy of a block with fresh ids (including column children). */
export const cloneBlock = (block: IEmailBlock): IEmailBlock => {
    if (block.type === EmailBlockTypeEnum.COLUMNS) {
        return {
            ...block,
            id: uuidv4(),
            columns: block.columns.map((col) => ({
                blocks: col.blocks.map((leaf) => ({ ...leaf, id: uuidv4() })),
            })) as IEmailColumnsBlock["columns"],
        };
    }

    return { ...block, id: uuidv4() };
};

/** Insert a copy right after the original. Returns the new block's id. */
export const duplicateBlock = (doc: IEmailDocument, id: string): { doc: IEmailDocument; newId: string | null } => {
    const loc = findBlock(doc, id);

    if (!loc) {
        return { doc, newId: null };
    }

    const copy = cloneBlock(loc.block);

    if (loc.parentId !== undefined && loc.columnIndex !== undefined) {
        const next = mapBlocks(doc, (block) => {
            if (block.id !== loc.parentId || block.type !== EmailBlockTypeEnum.COLUMNS) {
                return block;
            }

            const columns = block.columns.map((col, c) => {
                if (c !== loc.columnIndex) {
                    return col;
                }

                const idx = col.blocks.findIndex((b) => b.id === id);
                const blocks = [...col.blocks];
                blocks.splice(idx + 1, 0, copy as IEmailLeafBlock);

                return { blocks };
            }) as IEmailColumnsBlock["columns"];

            return { ...block, columns };
        });

        return { doc: next, newId: copy.id };
    }

    const idx = doc.blocks.findIndex((b) => b.id === id);
    const blocks = [...doc.blocks];
    blocks.splice(idx + 1, 0, copy);

    return { doc: { ...doc, blocks }, newId: copy.id };
};

/** Append (or insert at `index`) a top-level block. */
export const insertBlock = (doc: IEmailDocument, block: IEmailBlock, index?: number): IEmailDocument => {
    const blocks = [...doc.blocks];

    if (index === undefined || index < 0 || index > blocks.length) {
        blocks.push(block);
    } else {
        blocks.splice(index, 0, block);
    }

    return { ...doc, blocks };
};

/** Append a leaf block to one column of a columns block. */
export const addToColumn = (doc: IEmailDocument, columnsId: string, columnIndex: 0 | 1, block: IEmailLeafBlock): IEmailDocument => {
    return mapBlocks(doc, (b) => {
        if (b.id !== columnsId || b.type !== EmailBlockTypeEnum.COLUMNS) {
            return b;
        }

        const columns = b.columns.map((col, c) =>
            c === columnIndex ? { blocks: [...col.blocks, block] } : col
        ) as IEmailColumnsBlock["columns"];

        return { ...b, columns };
    });
};

/** Reorder top-level blocks (dnd-kit sortable semantics). */
export const moveTopLevel = (doc: IEmailDocument, activeId: string, overId: string): IEmailDocument => {
    const ids = doc.blocks.map((b) => b.id);
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);

    if (from === -1 || to === -1 || from === to) {
        return doc;
    }

    return { ...doc, blocks: arrayMove(doc.blocks, from, to) };
};

/** Reorder leaf blocks within one column. */
export const moveInColumn = (
    doc: IEmailDocument,
    columnsId: string,
    columnIndex: 0 | 1,
    activeId: string,
    overId: string
): IEmailDocument => {
    return mapBlocks(doc, (b) => {
        if (b.id !== columnsId || b.type !== EmailBlockTypeEnum.COLUMNS) {
            return b;
        }

        const columns = b.columns.map((col, c) => {
            if (c !== columnIndex) {
                return col;
            }

            const ids = col.blocks.map((leaf) => leaf.id);
            const from = ids.indexOf(activeId);
            const to = ids.indexOf(overId);

            if (from === -1 || to === -1 || from === to) {
                return col;
            }

            return { blocks: arrayMove(col.blocks, from, to) };
        }) as IEmailColumnsBlock["columns"];

        return { ...b, columns };
    });
};

/** Stable structural equality for dirty tracking. */
export const documentsEqual = (a: IEmailDocument | null | undefined, b: IEmailDocument | null | undefined): boolean => {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
};
